// Lógica pura de la grilla fiel del 120 (12 intervenciones nombradas × 24 horas).
import type { Procedure } from '@medplum/fhirtypes';
import { HORAS_DEL_DIA } from './enfermeria';

/** El papel trae 12 intervenciones por hoja. */
export const NUM_PANELES_120 = 12;

/** Una columna de la grilla: el nombre de la intervención y las horas (0-23) marcadas SIN guardar. */
export interface Panel120 {
  readonly nombre: string;
  readonly horas: readonly number[];
}

const nombreDe = (p: Procedure): string => (p.code?.text ?? '').trim();
const horaDe = (p: Procedure): number | undefined => (p.performedDateTime ? new Date(p.performedDateTime).getHours() : undefined);

/** Nombres distintos ya guardados en el día, en orden de aparición — ocupan las primeras columnas, bloqueadas. */
export function nombresGuardados(guardadas: readonly Procedure[]): string[] {
  const vistos = new Set<string>();
  return guardadas.map(nombreDe).filter((n) => {
    const k = n.toLowerCase();
    if (!n || vistos.has(k)) return false;
    vistos.add(k);
    return true;
  });
}

/** Horas ya guardadas para una intervención (mismo nombre, sin distinguir mayúsculas). */
export function horasGuardadas(guardadas: readonly Procedure[], nombre: string): Set<number> {
  const n = nombre.trim().toLowerCase();
  return new Set(guardadas.filter((p) => nombreDe(p).toLowerCase() === n).map(horaDe).filter((h): h is number => h !== undefined));
}

/** Celdas nuevas a guardar: paneles con nombre y horas marcadas que no estén ya guardadas. */
export function celdasNuevas(paneles: readonly Panel120[], guardadas: readonly Procedure[]): { readonly nombre: string; readonly hora: number }[] {
  return paneles.flatMap((p) => {
    const nombre = p.nombre.trim();
    if (!nombre) return [];
    const ya = horasGuardadas(guardadas, nombre);
    return HORAS_DEL_DIA.filter((h) => p.horas.includes(h) && !ya.has(h)).map((hora) => ({ nombre, hora }));
  });
}

export const panelesVacios = (n: number): Panel120[] => Array.from({ length: n }, () => ({ nombre: '', horas: [] }));

/** Responsable de una celda ya guardada: el `display` del primer `performer` (vacío si el Procedure no lo trae). */
export const responsableDe120 = (p: Procedure): string => p.performer?.[0]?.actor?.display?.trim() ?? '';

/** Responsable de la celda (intervención × hora) ya guardada; si hubiera varias del mismo nombre y hora, la primera. */
export function responsableGuardado(guardadas: readonly Procedure[], nombre: string, hora: number): string {
  const n = nombre.trim().toLowerCase();
  const p = guardadas.find((x) => nombreDe(x).toLowerCase() === n && horaDe(x) === hora);
  return p ? responsableDe120(p) : '';
}

/** Cuántas hojas (de 12 columnas) hacen falta para `columnas` intervenciones: la 13.ª pasa a una hoja de continuación. */
export const hojasNecesarias120 = (columnas: number): number => Math.max(1, Math.ceil(columnas / NUM_PANELES_120));

/** Una celda marcada de la grilla (intervención × hora) con su responsable (vacío si no se conoce). */
export interface Celda120 {
  readonly nombre: string;
  readonly hora: number;
  readonly responsable: string;
}

/** Las celdas (intervención × hora × responsable) de los Procedure guardados; descarta los que no traen nombre u hora. */
export function celdasDeProcedures(procs: readonly Procedure[]): Celda120[] {
  return procs.flatMap((p) => {
    const nombre = nombreDe(p);
    const hora = horaDe(p);
    return nombre && hora !== undefined ? [{ nombre, hora, responsable: responsableDe120(p) }] : [];
  });
}

const hh = (h: number): string => `${String(h).padStart(2, '0')}:00`;

/**
 * Pares del PDF firmado: TODAS las intervenciones del día (las ya guardadas y las de este guardado), no solo las del último guardado, con el
 * responsable de cada hora ("08:00 (Ana Pérez), 09:00 (Luis Mora)") como la celda RESPONSABLE del papel. Con más de 12 intervenciones cada
 * una lleva su hoja ("Hoja 2 — Intervención 13 — ..."). `sinResponsable` es el texto cuando la celda no tiene responsable y `etiquetaHoja` el rótulo (traducido) de la hoja.
 */
export function paresIntervenciones120(celdas: readonly Celda120[], etiquetaIntervencion: string, sinResponsable: string, etiquetaHoja: (n: number) => string): { pregunta: string; respuesta: string }[] {
  const porNombre = new Map<string, { nombre: string; horas: Map<number, string> }>();
  for (const c of celdas) {
    const clave = c.nombre.trim().toLowerCase();
    const g = porNombre.get(clave) ?? { nombre: c.nombre.trim(), horas: new Map<number, string>() };
    if (!g.horas.has(c.hora)) g.horas.set(c.hora, c.responsable || sinResponsable);
    porNombre.set(clave, g);
  }
  const grupos = [...porNombre.values()];
  return grupos.map((g, i) => {
    const horas = HORAS_DEL_DIA.filter((h) => g.horas.has(h)).map((h) => `${hh(h)} (${g.horas.get(h)})`);
    const hoja = grupos.length > NUM_PANELES_120 ? `${etiquetaHoja(Math.floor(i / NUM_PANELES_120) + 1)} — ` : '';
    return { pregunta: `${hoja}${etiquetaIntervencion} ${i + 1} — ${g.nombre}`, respuesta: horas.join(', ') };
  });
}

